import React, { createContext, useContext, useEffect, useState } from "react";
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential
} from "firebase/auth";
import { auth } from "../firebase";

interface AuthContextType {
  currentUser: User | null;
  signup: (email: string, password: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  function signup(email: string, password: string) {
    console.log('AuthContext - signup attempt:', email);
    return createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        console.log('AuthContext - signup successful:', userCredential.user.uid);
        return userCredential;
      })
      .catch((error) => {
        console.error('AuthContext - signup error:', error.code, error.message);
        throw error;
      });
  }

  function login(email: string, password: string) {
    console.log('AuthContext - login attempt:', email);
    return signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        console.log('AuthContext - login successful:', userCredential.user.uid);
        return userCredential;
      })
      .catch((error) => {
        console.error('AuthContext - login error:', error.code, error.message);
        throw error;
      });
  }

  function logout() {
    console.log('AuthContext - logout attempt');
    return signOut(auth)
      .then(() => {
        console.log('AuthContext - logout successful');
      })
      .catch((error) => {
        console.error('AuthContext - logout error:', error.code, error.message);
        throw error;
      });
  }

  useEffect(() => {
    console.log('AuthContext - Setting up auth state listener');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('AuthContext - Auth state changed:', user ? `User ${user.uid}` : 'No user');
      setCurrentUser(user);
      setLoading(false);
    });

    return () => {
      console.log('AuthContext - Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    loading
  };

  // Only render children when auth state is determined
  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-primary-light border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
} 